/**
 * Data Layer Snapshot
 *
 * Builds a PII-scrubbed, size-guarded snapshot of window.dataLayer
 * for use by the EventFoundry editor's Data Layer tab in ParamCapturePanel.
 */

const PII_KEY_PATTERN = /email|phone|address|first_name|last_name|street|zip|postal|city|country|user_id|customer_id/i;
const NOISE_EVENT_PATTERN = /^(gtm\.|consent|config|policy)/;

/**
 * Recursively walk an object, redacting PII keys and truncating depth/arrays.
 * @param {*} obj - The value to process
 * @param {number} depth - Current recursion depth (0-indexed)
 * @returns {*} The scrubbed value
 */
function redactPii(obj, depth = 0) {
  if (depth >= 5) return '[truncated]';
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.slice(0, 20).map(item => redactPii(item, depth + 1));
  }
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (PII_KEY_PATTERN.test(key)) {
      result[key] = '[redacted]';
    } else {
      result[key] = redactPii(value, depth + 1);
    }
  }
  return result;
}

/**
 * Remove top-level GTM bookkeeping fields like `gtm` and `gtm.uniqueEventId`.
 * @param {*} obj - The value to process
 * @returns {*} The payload without top-level GTM metadata
 */
function removeGTM(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => key !== 'gtm' && !key.startsWith('gtm.'))
  );
}

/**
 * Build a scrubbed, size-guarded snapshot of recent dataLayer events.
 * @returns {{ type: string, hasDataLayer: boolean, events: Array }}
 */
export function buildAnalyticsSnapshot() {
  if (!Array.isArray(window.dataLayer)) {
    return { type: 'DATA_LAYER_SNAPSHOT_RESPONSE', hasDataLayer: false, events: [] };
  }

  const recentEntries = window.dataLayer.slice(-50);

  const seenNames = new Map(); // name -> { count, latestPayload }
  for (let i = recentEntries.length - 1; i >= 0; i--) {
    const entry = recentEntries[i];
    if (!entry || typeof entry.event !== 'string') continue;

    if (NOISE_EVENT_PATTERN.test(entry.event)) continue;

    const existing = seenNames.get(entry.event);
    if (!existing) {
      seenNames.set(entry.event, { count: 1, latestPayload: entry });
    } else {
      existing.count++;
    }
  }

  const events = Array.from(seenNames.entries()).map(([name, { count, latestPayload }]) => {
    const payloadWithoutGTM = removeGTM(latestPayload);
    const sanitizedPayload = redactPii(payloadWithoutGTM);

    return {
      name,
      count,
      latestPayload: sanitizedPayload,
    };
  });

  const response = { type: 'DATA_LAYER_SNAPSHOT_RESPONSE', hasDataLayer: true, events };
  if (JSON.stringify(response).length > 100_000) {
    for (let i = events.length - 1; i >= 0; i--) {
      events[i].latestPayload = '[truncated]';
      if (JSON.stringify(response).length <= 100_000) break;
    }
  }

  return response;
}
